import React, { useState, useEffect, useCallback } from 'react';
import CityAutocomplete from '../../standouts/components/CityAutocomplete';
import DashboardHeader from '../components/DashboardHeader';
import DashboardSidebar from '../components/DashboardSidebar';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../config/supabase';
import { SPORTS } from '../../standouts/data/standoutsConstants';

const EMPTY_FORM = {
  name: '', sport: '', city: '', state: '',
  contact_name: '', contact_email: '', phone: '', website: '',
};

const OrganizationsScreen = () => {
  const { getOrganizations, createOrganization, updateOrganizationStatus, deleteOrganization } = useAuth();

  const [activeItem, setActiveItem] = useState('organizations');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // User join requests state
  const [joinRequests, setJoinRequests] = useState([]);
  const [joinRequestsLoading, setJoinRequestsLoading] = useState(false);
  const [joinActionLoading, setJoinActionLoading] = useState(null);
  const [joinConfirmModal, setJoinConfirmModal] = useState(null); // { action, requestId, userId, orgId, userName, orgName }
  const [joinSuccessModal, setJoinSuccessModal] = useState(null); // { message, type }
  const [membersModal, setMembersModal] = useState(null); // { org, members, loading }
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [selectedOrgForAddUser, setSelectedOrgForAddUser] = useState(null);
  const [approvedUsers, setApprovedUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [addingUser, setAddingUser] = useState(false);

  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(() => setNotification({ show: false, type: '', message: '' }), 4000);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    const result = await getOrganizations();
    if (result.success) setData(result.data || []);
    setLoading(false);
  }, [getOrganizations]);

  useEffect(() => {
    fetchData();
    fetchJoinRequests();
  }, [fetchData]);

  const fetchJoinRequests = async () => {
    try {
      setJoinRequestsLoading(true);

      // Use RPC function that bypasses RLS (since admin auth doesn't use Supabase Auth)
      const { data, error } = await supabase.rpc('get_admin_join_requests');

      console.log('Join requests RPC result:', { data, error });

      if (error) throw error;

      if (!data || data.length === 0) {
        console.log('No pending join requests found');
        setJoinRequests([]);
        return;
      }

      console.log(`Found ${data.length} pending join requests`);

      // Transform the data to match the expected format
      const formattedData = data.map(req => ({
        id: req.id,
        user_id: req.user_id,
        organization_id: req.organization_id,
        status: req.status,
        request_type: req.request_type,
        created_at: req.created_at,
        updated_at: req.updated_at,
        organization: {
          id: req.organization_id,
          name: req.org_name,
          sport: req.org_sport,
          city: req.org_city,
          state: req.org_state,
        },
        user_name: `${req.user_first_name || ''} ${req.user_last_name || ''}`.trim() || 'Unknown',
        user_email: req.user_email || '',
        user_role: req.user_role || 'unknown',
      }));

      console.log('Formatted data:', formattedData);
      setJoinRequests(formattedData);
    } catch (err) {
      console.error('Error fetching join requests:', err);
    } finally {
      setJoinRequestsLoading(false);
    }
  };

  const openMembersModal = async (org) => {
    setMembersModal({ org, members: [], loading: true });
    try {
      const { data, error } = await supabase.rpc('get_org_members', { p_organization_id: org.id });
      if (error) throw error;
      setMembersModal({ org, members: data || [], loading: false });
    } catch (err) {
      // fallback: query user_organizations directly
      try {
        const { data: uoData, error: uoErr } = await supabase
          .from('user_organizations')
          .select('user_id, status, request_type, created_at')
          .eq('organization_id', org.id)
          .eq('status', 'approved');
        if (uoErr) throw uoErr;
        // get all users and cross-reference
        const { data: allUsers } = await supabase.rpc('get_all_phase0_users');
        const userMap = {};
        (allUsers || []).forEach(u => { userMap[u.user_id] = u; });
        const members = (uoData || []).map(uo => ({ ...userMap[uo.user_id], joined_org_at: uo.created_at })).filter(Boolean);
        setMembersModal({ org, members, loading: false });
      } catch (_) {
        setMembersModal({ org, members: [], loading: false });
      }
    }
  };

  const fetchApprovedUsers = async () => {
    try {
      const { data, error } = await supabase.rpc('get_all_phase0_users');
      if (error) throw error;
      setApprovedUsers((data || []).filter(u => u.approved));
    } catch (err) {
      console.error('Error fetching approved users:', err);
    }
  };

  const handleApproveJoinRequest = async () => {
    const { requestId, userId, orgId, orgName } = joinConfirmModal;
    setJoinConfirmModal(null);
    try {
      setJoinActionLoading(requestId);
      const { data, error } = await supabase.rpc('admin_approve_join_request', { p_request_id: requestId });
      if (error) throw error;
      if (!data) throw new Error('Failed to approve request');
      try {
        await supabase.functions.invoke('send-org-approval-email', { body: { userId, organizationId: orgId, organizationName: orgName || 'Organization' } });
      } catch (_) {}
      await fetchJoinRequests();
      setJoinSuccessModal({ message: `User has been approved to join ${orgName}. An email has been sent.`, type: 'approve' });
    } catch (err) {
      showNotification('error', err.message || 'Failed to approve request');
    } finally {
      setJoinActionLoading(null);
    }
  };

  const handleRejectJoinRequest = async () => {
    const { requestId, userName, orgName } = joinConfirmModal;
    setJoinConfirmModal(null);
    try {
      setJoinActionLoading(requestId);
      const { data, error } = await supabase.rpc('admin_reject_join_request', { p_request_id: requestId });
      if (error) throw error;
      if (!data) throw new Error('Failed to reject request');
      await fetchJoinRequests();
      setJoinSuccessModal({ message: `${userName}'s request to join ${orgName} has been rejected.`, type: 'reject' });
    } catch (err) {
      showNotification('error', err.message || 'Failed to reject request');
    } finally {
      setJoinActionLoading(null);
    }
  };

  const handleAddUserToOrg = async () => {
    if (!selectedUser || !selectedOrgForAddUser) {
      showNotification('error', 'Please select a user');
      return;
    }

    try {
      setAddingUser(true);

      // Use RPC function to bypass RLS
      const { data, error } = await supabase.rpc('admin_add_user_to_organization', {
        p_user_id: selectedUser.user_id,
        p_organization_id: selectedOrgForAddUser.id,
        p_request_type: 'join_existing'
      });

      if (error) {
        // Check for duplicate error from the function
        if (error.message && error.message.includes('already in this organization')) {
          showNotification('error', 'User is already in this organization');
        } else {
          throw error;
        }
      } else {
        const addedName = `${selectedUser.first_name} ${selectedUser.last_name}`;
        const addedOrg = selectedOrgForAddUser.name;
        setShowAddUserModal(false);
        setSelectedUser(null);
        setSelectedOrgForAddUser(null);
        setJoinSuccessModal({ message: `${addedName} has been successfully added to ${addedOrg}.`, type: 'approve' });
      }
    } catch (err) {
      console.error('Error adding user to org:', err);
      showNotification('error', err.message || 'Failed to add user');
    } finally {
      setAddingUser(false);
    }
  };

  const filtered = data.filter((o) => {
    const tabMatch = activeTab === 'all' || o.status === activeTab;
    const searchMatch = !search || (o.name || '').toLowerCase().includes(search.toLowerCase());
    return tabMatch && searchMatch;
  });

  const counts = {
    all: data.length,
    pending: data.filter((o) => o.status === 'pending').length,
    approved: data.filter((o) => o.status === 'approved').length,
  };

  const handleApprove = async (id) => {
    const result = await updateOrganizationStatus(id, 'approved');
    if (result.success) { await fetchData(); showNotification('success', 'Organization approved.'); }
    else showNotification('error', result.error || 'Failed');
  };

  const handleReject = async (id) => {
    const result = await updateOrganizationStatus(id, 'rejected');
    if (result.success) { await fetchData(); showNotification('success', 'Organization rejected.'); }
    else showNotification('error', result.error || 'Failed');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this organization permanently?')) return;
    const result = await deleteOrganization(id);
    if (result.success) { await fetchData(); showNotification('success', 'Organization deleted.'); }
    else showNotification('error', result.error || 'Failed');
  };

  const handleCreate = async () => {
    if (!form.name.trim()) { showNotification('error', 'Organization name is required'); return; }
    setSaving(true);
    const result = await createOrganization(form);
    setSaving(false);
    if (result.success) {
      setShowModal(false);
      setForm(EMPTY_FORM);
      await fetchData();
      showNotification('success', 'Organization created.');
    } else {
      showNotification('error', result.error || 'Failed to create');
    }
  };

  const STATUS_TABS = [
    { id: 'all', label: 'All' },
    { id: 'pending', label: 'Pending' },
    { id: 'approved', label: 'Approved' },
  ];

  return (
    <div className="dashboard-layout">
      <DashboardSidebar activeItem={activeItem} setActiveItem={setActiveItem} isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />

      <div className="dashboard-main">
        <DashboardHeader
          toggleSidebar={() => setSidebarOpen(!sidebarOpen)}
          title="Organizations"
          subtitle="Manage approved organizations and review requests"
        />

        <div className="standouts-screen">

          {notification.show && (
            <div className={`ss-notification ss-notification-${notification.type}`}>{notification.message}</div>
          )}

          {/* Stats */}
          <div className="ss-stats-row">
            {STATUS_TABS.map((t) => (
              <div key={t.id} className={`ss-stat-card ${activeTab === t.id ? 'active' : ''}`} onClick={() => setActiveTab(t.id)}>
                <span className="ss-stat-count">{counts[t.id] ?? 0}</span>
                <span className="ss-stat-label">{t.label}</span>
              </div>
            ))}
          </div>

          {/* Controls */}
          <div className="ss-controls">
            <input
              type="text"
              className="ss-search"
              placeholder="Search organizations..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button className="ss-create-btn" onClick={() => { setForm(EMPTY_FORM); setShowModal(true); }}>
              + Create Organization
            </button>
          </div>

          {/* Tabs */}
          <div className="ss-tabs">
            {STATUS_TABS.map((t) => (
              <button key={t.id} className={`ss-tab ${activeTab === t.id ? 'active' : ''}`} onClick={() => setActiveTab(t.id)}>
                {t.label}<span className="ss-tab-count">{counts[t.id] ?? 0}</span>
              </button>
            ))}
          </div>

          {/* Table */}
          {loading ? (
            <div className="ss-empty">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="ss-empty">No organizations found.</div>
          ) : (
            <div className="ss-table-wrap">
              <table className="ss-table">
                <thead>
                  <tr>
                    <th>Organization</th>
                    <th>Sport</th>
                    <th>Location</th>
                    <th>Contact</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((o) => (
                    <tr key={o.id}>
                      <td>
                        <strong style={{ color: '#fff', fontSize: '13px' }}>{o.name}</strong>
                        {o.website && <span style={{ display: 'block', fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginTop: '2px' }}>{o.website}</span>}
                      </td>
                      <td style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)' }}>{o.sport || '—'}</td>
                      <td style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)' }}>
                        {[o.city, o.state].filter(Boolean).join(', ') || '—'}
                      </td>
                      <td>
                        <span style={{ display: 'block', fontSize: '12px', color: 'rgba(255,255,255,0.8)' }}>{o.contact_name || '—'}</span>
                        <span style={{ display: 'block', fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>{o.contact_email || ''}</span>
                      </td>
                      <td>
                        <span className={`ss-status-pill ss-status-${o.status}`}>{o.status}</span>
                      </td>
                      <td>
                        <div className="ss-actions">
                          {o.status === 'approved' && (
                            <button
                              onClick={() => openMembersModal(o)}
                              title="View Members"
                              style={{ padding: '5px 10px', background: 'rgba(243,177,41,0.12)', border: '1px solid rgba(243,177,41,0.3)', borderRadius: '4px', color: '#f3b129', fontSize: '11px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '4px' }}
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                              Members
                            </button>
                          )}
                          {o.status === 'pending' && (
                            <button className="ss-action-btn approve" onClick={() => handleApprove(o.id)} title="Approve">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                            </button>
                          )}
                          {o.status === 'pending' && (
                            <button className="ss-action-btn reject" onClick={() => handleReject(o.id)} title="Reject">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            </button>
                          )}
                          {o.status === 'approved' && (
                            <button className="ss-action-btn reject" onClick={() => handleReject(o.id)} title="Revoke">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            </button>
                          )}
                          <button className="ss-action-btn delete" onClick={() => handleDelete(o.id)} title="Delete">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* User Join Requests Section */}
          <div style={{ marginTop: '40px' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
            }}>
              <div>
                <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#fff', marginBottom: '4px' }}>
                  User Join Requests
                </h2>
                <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)' }}>
                  Approve or reject users requesting to join organizations
                </p>
              </div>
              <button
                className="ss-create-btn"
                onClick={() => {
                  setSelectedOrgForAddUser(null);
                  setSelectedUser(null);
                  setShowAddUserModal(true);
                  fetchApprovedUsers();
                }}
              >
                + Add User to Organization
              </button>
            </div>

            {joinRequestsLoading ? (
              <div className="ss-table-wrap">
                <table className="ss-table">
                  <thead><tr>{['User','Role','Organization','Request Type','Requested','Actions'].map(h=><th key={h}>{h}</th>)}</tr></thead>
                  <tbody>
                    {Array.from({length:4}).map((_,i)=>(
                      <tr key={i} style={{borderBottom:'1px solid rgba(255,255,255,0.05)'}}>
                        {[160,70,180,100,80,70].map((w,j)=>(
                          <td key={j} style={{padding:'16px'}}>
                            <div style={{height:'13px',width:`${w}px`,borderRadius:'5px',background:'rgba(255,255,255,0.06)',animation:'pulse 1.5s ease-in-out infinite'}}/>
                            {j===0&&<div style={{height:'10px',width:'120px',borderRadius:'5px',background:'rgba(255,255,255,0.04)',marginTop:'6px',animation:'pulse 1.5s ease-in-out infinite'}}/>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}`}</style>
              </div>
            ) : joinRequests.length === 0 ? (
              <div className="ss-empty">No pending join requests.</div>
            ) : (
              <div className="ss-table-wrap">
                <table className="ss-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Role</th>
                      <th>Organization</th>
                      <th>Request Type</th>
                      <th>Requested</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {joinRequests.map((req) => (
                      <tr key={req.id}>
                        <td>
                          <strong style={{ color: '#fff', fontSize: '13px' }}>{req.user_name}</strong>
                          <span style={{ display: 'block', fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginTop: '2px' }}>
                            {req.user_email}
                          </span>
                        </td>
                        <td style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)' }}>
                          <span style={{
                            padding: '4px 8px',
                            background: 'rgba(212, 165, 116, 0.2)',
                            color: '#d4a574',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '600',
                          }}>
                            {req.user_role === 'league_admin' ? 'League Admin' :
                             req.user_role.charAt(0).toUpperCase() + req.user_role.slice(1)}
                          </span>
                        </td>
                        <td>
                          <strong style={{ color: '#fff', fontSize: '13px' }}>
                            {req.organization?.name || 'Unknown'}
                          </strong>
                          <span style={{ display: 'block', fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginTop: '2px' }}>
                            {req.organization?.sport && `${req.organization.sport} • `}
                            {[req.organization?.city, req.organization?.state].filter(Boolean).join(', ')}
                          </span>
                        </td>
                        <td>
                          <span style={{
                            padding: '4px 8px',
                            background: req.request_type === 'create_new' ? 'rgba(168, 85, 247, 0.2)' : 'rgba(59, 130, 246, 0.2)',
                            color: req.request_type === 'create_new' ? '#a855f7' : '#3b82f6',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '600',
                          }}>
                            {req.request_type === 'create_new' ? 'New Org' : 'Join Existing'}
                          </span>
                        </td>
                        <td style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)' }}>
                          {new Date(req.created_at).toLocaleDateString()}
                        </td>
                        <td>
                          <div className="ss-actions">
                            <button
                              className="ss-action-btn approve"
                              disabled={joinActionLoading === req.id}
                              onClick={() => setJoinConfirmModal({ action: 'approve', requestId: req.id, userId: req.user_id, orgId: req.organization_id, userName: req.user_name, orgName: req.organization?.name || 'Organization' })}
                              title="Approve"
                            >
                              {joinActionLoading === req.id ? '…' : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>}
                            </button>
                            <button
                              className="ss-action-btn reject"
                              disabled={joinActionLoading === req.id}
                              onClick={() => setJoinConfirmModal({ action: 'reject', requestId: req.id, userId: req.user_id, orgId: req.organization_id, userName: req.user_name, orgName: req.organization?.name || 'Organization' })}
                              title="Reject"
                            >
                              {joinActionLoading === req.id ? '…' : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Members Modal */}
      {membersModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: '#0e0e0e', border: '1px solid rgba(243,177,41,0.2)', borderRadius: '16px', width: '100%', maxWidth: '560px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <div style={{ padding: '24px 24px 16px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h2 style={{ color: '#fff', fontSize: '17px', fontWeight: '700', marginBottom: '4px' }}>{membersModal.org.name}</h2>
                <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '13px' }}>
                  {membersModal.org.sport && `${membersModal.org.sport} • `}{[membersModal.org.city, membersModal.org.state].filter(Boolean).join(', ')}
                  {!membersModal.loading && <span style={{ marginLeft: '8px', color: '#f3b129', fontWeight: '600' }}>{membersModal.members.length} member{membersModal.members.length !== 1 ? 's' : ''}</span>}
                </p>
              </div>
              <button onClick={() => setMembersModal(null)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: '20px', cursor: 'pointer', lineHeight: 1, padding: '0 4px' }}>×</button>
            </div>

            {/* Body */}
            <div style={{ overflowY: 'auto', padding: '16px 24px 24px' }}>
              {membersModal.loading ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px' }}>
                      <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(255,255,255,0.07)', animation: 'pulse 1.5s ease-in-out infinite', flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ height: '13px', width: '140px', borderRadius: '5px', background: 'rgba(255,255,255,0.07)', marginBottom: '6px', animation: 'pulse 1.5s ease-in-out infinite' }} />
                        <div style={{ height: '11px', width: '180px', borderRadius: '5px', background: 'rgba(255,255,255,0.04)', animation: 'pulse 1.5s ease-in-out infinite' }} />
                      </div>
                    </div>
                  ))}
                  <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}`}</style>
                </div>
              ) : membersModal.members.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px 0', color: 'rgba(255,255,255,0.35)', fontSize: '14px' }}>No members in this organization yet.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {membersModal.members.map((m, i) => (
                    <div key={m.user_id || i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px' }}>
                      <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(243,177,41,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '14px', fontWeight: '700', color: '#f3b129' }}>
                        {(m.first_name || '?')[0].toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#fff' }}>{m.first_name} {m.last_name}</div>
                        <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.email}</div>
                      </div>
                      <span style={{ padding: '3px 8px', background: 'rgba(212,165,116,0.15)', color: '#d4a574', borderRadius: '4px', fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                        {m.role === 'league_admin' ? 'League Admin' : m.role ? m.role.charAt(0).toUpperCase() + m.role.slice(1) : '—'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Join Request Confirm Modal */}
      {joinConfirmModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#0e0e0e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px', padding: '32px 28px', maxWidth: '380px', width: '90%', textAlign: 'center' }}>
            <div style={{ width: '52px', height: '52px', borderRadius: '50%', margin: '0 auto 18px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: joinConfirmModal.action === 'approve' ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)' }}>
              {joinConfirmModal.action === 'approve'
                ? <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                : <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              }
            </div>
            <h2 style={{ color: '#fff', fontSize: '17px', fontWeight: '700', marginBottom: '8px' }}>
              {joinConfirmModal.action === 'approve' ? 'Approve Join Request?' : 'Reject Join Request?'}
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', lineHeight: '1.6', marginBottom: '24px' }}>
              {joinConfirmModal.action === 'approve'
                ? <><span style={{ color: '#fff', fontWeight: '600' }}>{joinConfirmModal.userName}</span> will be approved to join <span style={{ color: '#f3b129', fontWeight: '600' }}>{joinConfirmModal.orgName}</span>.</>
                : <>Reject <span style={{ color: '#fff', fontWeight: '600' }}>{joinConfirmModal.userName}</span>'s request to join <span style={{ color: '#f3b129', fontWeight: '600' }}>{joinConfirmModal.orgName}</span>?</>
              }
            </p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setJoinConfirmModal(null)} style={{ flex: 1, padding: '10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: 'rgba(255,255,255,0.7)', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>Cancel</button>
              <button
                onClick={joinConfirmModal.action === 'approve' ? handleApproveJoinRequest : handleRejectJoinRequest}
                style={{ flex: 1, padding: '10px', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', background: joinConfirmModal.action === 'approve' ? '#22c55e' : '#ef4444', color: '#fff' }}
              >
                {joinConfirmModal.action === 'approve' ? 'Approve' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Join Request Success Modal */}
      {joinSuccessModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#0e0e0e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px', padding: '32px 28px', maxWidth: '360px', width: '90%', textAlign: 'center' }}>
            <div style={{ width: '52px', height: '52px', borderRadius: '50%', margin: '0 auto 18px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: joinSuccessModal.type === 'approve' ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={joinSuccessModal.type === 'approve' ? '#22c55e' : '#ef4444'} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
            <h2 style={{ color: '#fff', fontSize: '17px', fontWeight: '700', marginBottom: '8px' }}>Done!</h2>
            <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '14px', lineHeight: '1.6', marginBottom: '24px' }}>{joinSuccessModal.message}</p>
            <button onClick={() => setJoinSuccessModal(null)} style={{ background: '#f3b129', color: '#000', border: 'none', borderRadius: '8px', padding: '10px 28px', fontSize: '14px', fontWeight: '700', cursor: 'pointer' }}>Got it</button>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showModal && (
        <div className="ss-modal-overlay" onClick={() => !saving && setShowModal(false)}>
          <div className="ss-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ss-modal-header">
              <h2>Create Organization</h2>
              <button className="ss-modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="ss-modal-body">
              <div className="ss-modal-section">
                <h3>Organization Info</h3>
                <div className="ss-form-row">
                  <label className="ss-form-field">
                    <span>Organization Name *</span>
                    <input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="e.g. Youth Flag Football League" />
                  </label>
                  <label className="ss-form-field">
                    <span>Sport</span>
                    <select value={form.sport} onChange={(e) => setForm((p) => ({ ...p, sport: e.target.value }))}>
                      <option value="">Select sport</option>
                      {SPORTS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </label>
                </div>
                <CityAutocomplete
                  city={form.city}
                  state={form.state}
                  onCityChange={(val) => setForm((p) => ({ ...p, city: val }))}
                  onStateChange={(val) => setForm((p) => ({ ...p, state: val }))}
                  labelClassName="ss-form-field"
                  required={false}
                />
              </div>

              <div className="ss-modal-section">
                <h3>Contact Info</h3>
                <div className="ss-form-row">
                  <label className="ss-form-field">
                    <span>Contact Name</span>
                    <input value={form.contact_name} onChange={(e) => setForm((p) => ({ ...p, contact_name: e.target.value }))} />
                  </label>
                  <label className="ss-form-field">
                    <span>Contact Email</span>
                    <input type="email" value={form.contact_email} onChange={(e) => setForm((p) => ({ ...p, contact_email: e.target.value }))} />
                  </label>
                </div>
                <div className="ss-form-row">
                  <label className="ss-form-field">
                    <span>Phone</span>
                    <input value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} />
                  </label>
                  <label className="ss-form-field">
                    <span>Website or Instagram (optional)</span>
                    <input value={form.website} onChange={(e) => setForm((p) => ({ ...p, website: e.target.value }))} placeholder="https:// or @handle" />
                  </label>
                </div>
              </div>
            </div>
            <div className="ss-modal-footer">
              <button className="ss-modal-cancel" onClick={() => setShowModal(false)} disabled={saving}>Cancel</button>
              <button className="ss-modal-save" onClick={handleCreate} disabled={saving}>
                {saving ? 'Creating...' : 'Create Organization'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add User to Organization Modal */}
      {showAddUserModal && (
        <div className="ss-modal-overlay" onClick={() => !addingUser && setShowAddUserModal(false)}>
          <div className="ss-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ss-modal-header">
              <h2>Add User to Organization</h2>
              <button className="ss-modal-close" onClick={() => setShowAddUserModal(false)}>×</button>
            </div>
            <div className="ss-modal-body">
              <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)', marginBottom: '20px' }}>
                Manually add an approved user to an organization without requiring approval.
              </p>

              <div className="ss-modal-section">
                <h3>Select Organization</h3>
                <div className="ss-form-row">
                  <label className="ss-form-field">
                    <span>Organization *</span>
                    <select
                      value={selectedOrgForAddUser?.id || ''}
                      onChange={(e) => {
                        const org = data.find(o => o.id === e.target.value);
                        setSelectedOrgForAddUser(org || null);
                      }}
                    >
                      <option value="">Select an organization</option>
                      {data.filter(o => o.status === 'approved').map((org) => (
                        <option key={org.id} value={org.id}>
                          {org.name} {org.sport && `(${org.sport})`}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>

              <div className="ss-modal-section">
                <h3>Select User</h3>
                <div className="ss-form-row">
                  <label className="ss-form-field">
                    <span>User *</span>
                    <select
                      value={selectedUser?.user_id || ''}
                      onChange={(e) => {
                        const user = approvedUsers.find(u => u.user_id === e.target.value);
                        setSelectedUser(user || null);
                      }}
                      disabled={!selectedOrgForAddUser}
                    >
                      <option value="">Select a user</option>
                      {approvedUsers.map((user) => (
                        <option key={user.user_id} value={user.user_id}>
                          {user.first_name} {user.last_name} ({user.role}) - {user.email}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                {approvedUsers.length === 0 && (
                  <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginTop: '8px' }}>
                    No approved users available. Users must be approved in the Users page first.
                  </p>
                )}
              </div>

              {selectedUser && selectedOrgForAddUser && (
                <div style={{
                  padding: '12px',
                  background: 'rgba(212, 165, 116, 0.1)',
                  border: '1px solid rgba(212, 165, 116, 0.3)',
                  borderRadius: '6px',
                  marginTop: '16px',
                }}>
                  <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', marginBottom: '4px' }}>
                    Ready to add:
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#fff' }}>
                    {selectedUser.first_name} {selectedUser.last_name}
                  </div>
                  <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>
                    to {selectedOrgForAddUser.name}
                  </div>
                </div>
              )}
            </div>
            <div className="ss-modal-footer">
              <button
                className="ss-modal-cancel"
                onClick={() => setShowAddUserModal(false)}
                disabled={addingUser}
              >
                Cancel
              </button>
              <button
                className="ss-modal-save"
                onClick={handleAddUserToOrg}
                disabled={addingUser || !selectedUser || !selectedOrgForAddUser}
              >
                {addingUser ? 'Adding...' : 'Add User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationsScreen;
